import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllArticles, getAllEPapers, getAllAds,
  getVisitStats, getActiveVisitors
} from '../../firebase/firestore';
import './Dashboard.css';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [counts, setCounts] = useState({ articles: 0, epapers: 0, ads: 0 });
  const [activeVisitors, setActiveVisitors] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [articles, epapers, ads, visitStats, active] = await Promise.all([
        getAllArticles(),
        getAllEPapers(),
        getAllAds(),
        getVisitStats(),
        getActiveVisitors()
      ]);
      setCounts({
        articles: articles.length,
        published: articles.filter(a => a.status === 'published').length,
        epapers: epapers.length,
        ads: ads.filter(a => a.isActive).length
      });
      setStats(visitStats);
      setActiveVisitors(active);
      setLoading(false);
    };
    load();

    const interval = setInterval(async () => {
      const active = await getActiveVisitors();
      setActiveVisitors(active);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="loading-spinner">Loading dashboard...</div>;

  const STAT_CARDS = [
    { label: 'Published Articles', value: counts.published, icon: '📰', color: 'blue', link: '/admin/manage-articles' },
    { label: 'Total Visits Today', value: stats?.today?.total || 0, icon: '👁', color: 'green', link: '/admin/analytics' },
    { label: 'Active Ads', value: counts.ads, icon: '📢', color: 'gold', link: '/admin/advertisements' },
    { label: 'E-Papers Uploaded', value: counts.epapers, icon: '📄', color: 'purple', link: '/admin/epapers' },
    { label: 'This Week Visits', value: stats?.week?.total || 0, icon: '📅', color: 'teal', link: '/admin/analytics' },
    { label: 'This Month Visits', value: stats?.month?.total || 0, icon: '📆', color: 'orange', link: '/admin/analytics' },
  ];

  const QUICK_ACTIONS = [
    { to: '/admin/add-article', icon: '✏️', label: 'Write New Article', desc: 'Publish a news story' },
    { to: '/admin/epapers', icon: '📄', label: 'Upload E-Paper', desc: "Add today's newspaper" },
    { to: '/admin/advertisements', icon: '📢', label: 'Manage Ads', desc: 'Add or edit advertisements' },
    { to: '/admin/analytics', icon: '📈', label: 'View Analytics', desc: 'See visit statistics' },
    { to: '/admin/categories', icon: '🏷️', label: 'Categories', desc: 'Manage news categories' },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back to NewsBuzz Today admin panel</p>
        </div>
        <div className="active-badge">
          <span className="active-dot" />
          <span>{activeVisitors} active now</span>
        </div>
      </div>

      <div className="stat-cards-grid">
        {STAT_CARDS.map(card => (
          <Link key={card.label} to={card.link} className={`stat-card color-${card.color}`}>
            <div className="stat-icon">{card.icon}</div>
            <div className="stat-info">
              <div className="stat-value">{card.value.toLocaleString()}</div>
              <div className="stat-label">{card.label}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="dashboard-section">
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions-grid">
          {QUICK_ACTIONS.map(action => (
            <Link key={action.to} to={action.to} className="quick-action-card">
              <span className="qa-icon">{action.icon}</span>
              <div>
                <div className="qa-label">{action.label}</div>
                <div className="qa-desc">{action.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="visit-summary">
        <h2 className="section-title">Visit Summary</h2>
        <div className="visit-table">
          <div className="vt-header">
            <span>Period</span><span>Total</span><span>Main Site</span><span>E-Paper</span>
          </div>
          {[
            { label: 'Today', data: stats?.today },
            { label: 'This Week', data: stats?.week },
            { label: 'This Month', data: stats?.month },
            { label: 'This Year', data: stats?.year },
            { label: 'All Time', data: stats?.allTime },
          ].map(row => (
            <div key={row.label} className="vt-row">
              <span className="vt-period">{row.label}</span>
              <span className="vt-val total">{(row.data?.total || 0).toLocaleString()}</span>
              <span className="vt-val">{(row.data?.site || 0).toLocaleString()}</span>
              <span className="vt-val epaper">{(row.data?.epaper || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
